import React, { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Phone, Mail, Facebook, Twitter, Instagram, MapPin, Linkedin, Youtube, Music } from 'lucide-react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { NAVIGATION_ITEMS } from '../../utils/constants'
import { useSectionContent } from '../../hooks/useContent'
import { useLogoSettings } from '../../hooks/useLogoSettings'
import { useSocialMediaLinks } from '../../hooks/useSocialMediaLinks'
import Button from '../ui/Button'
import { ReactComponent as NepalFlag } from '../../Flag_of_Nepal.svg'

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const prefersReducedMotion = useReducedMotion()
  const location = useLocation()
  const { getContent, loading } = useSectionContent('contact')
  const { logoSettings, loading: logoLoading } = useLogoSettings()
  const { activeSocialLinks, socialSettings, loading: socialLoading } = useSocialMediaLinks()

  // Icon mapping for social media
  const getIcon = (iconName: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      Facebook,
      Twitter,
      Instagram,
      Linkedin,
      Youtube,
      Music // TikTok uses Music icon
    }
    const IconComponent = iconMap[iconName] || Facebook
    return IconComponent
  }
  // Memoize contact information to prevent unnecessary re-renders
  const contactInfo = useMemo(() => ({
    phone: loading ? '+977 9867242846' : getContent('phone', '+977 9867242846'),
    email: loading ? 'info@nwlc.com.np' : getContent('email', 'info@nwlc.com.np'),
    hours: loading ? 'Monday - Friday, 08am - 05pm' : getContent('hours', 'Monday - Friday, 08am - 05pm'),
    address: loading ? 'Buddhanagar, New Baneshwor' : getContent('address', 'Buddhanagar, New Baneshwor')
  }), [loading, getContent])


  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-white/95 shadow-sm backdrop-blur-xl">
      {/* Top Info Bar */}
      <div className="hidden bg-gradient-to-r from-[#8f0d28] via-crimson to-[#f02d55] py-2 text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)] lg:block">
        <div className="container">
          <div className="flex justify-between items-center text-sm">
            {/* Contact Info */}
            <div className="flex items-center space-x-6">
              <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2 hover:text-white/80 transition-colors">
                <Phone className="w-4 h-4 text-white" />
                <span>{contactInfo.phone}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center space-x-2 hover:text-white/80 transition-colors">
                <Mail className="w-4 h-4 text-white" />
                <span>{contactInfo.email}</span>
              </a>
              <a href="https://maps.app.goo.gl/JEGWuKF1X14tMd3J7" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-white/80 transition-colors">
                <MapPin className="w-4 h-4 text-white" />
                <span>{contactInfo.address}</span>
              </a>
            </div>
            
            {/* Social Links & Login */}
            <div className="flex items-center space-x-4">
              {/* Social Media Icons - Only show if header display is enabled */}
              {socialSettings.show_social_links_header && (
                <div className="flex items-center space-x-1">
                  {!socialLoading && activeSocialLinks.length > 0 ? (
                    // Show admin-managed social links when available
                    activeSocialLinks.map((link) => {
                      const IconComponent = getIcon(link.icon_name)
                      return (
                        <a
                          key={link.id}
                          href={link.url}
                          className="p-1 hover:text-white/80 transition-colors"
                          title={link.platform_name}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconComponent className="w-4 h-4" />
                        </a>
                      )
                    })
                  ) : (
                    // Show default social links when no admin links are configured
                    <>
                      <a
                        href="https://facebook.com"
                        className="p-1 hover:text-white/80 transition-colors"
                        title="Facebook"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Facebook className="w-4 h-4" />
                      </a>
                      <a
                        href="https://twitter.com"
                        className="p-1 hover:text-white/80 transition-colors"
                        title="Twitter"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Twitter className="w-4 h-4" />
                      </a>
                      <a
                        href="https://instagram.com"
                        className="p-1 hover:text-white/80 transition-colors"
                        title="Instagram"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                      <a
                        href="https://linkedin.com"
                        className="p-1 hover:text-white/80 transition-colors"
                        title="LinkedIn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                    </>
                  )}
                </div>
              )}
              <div className="h-4 w-px bg-white/30"></div>
              <div className="flex items-center space-x-3 text-xs">
                <Link to="/admission" className="hover:text-white/80 transition-colors">Admission Center</Link>
                <div className="h-3 w-px bg-white/30"></div>
                <Link to="/admin" className="hover:text-white/80 transition-colors">Login</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="border-b border-gray-100 bg-white/95">
        {/* Subtle cultural accent */}
        <div className="h-0.5 bg-gradient-to-r from-crimson/20 via-mustard/20 to-crimson/20"></div>
        
        <div className="container">
          <div className="flex h-20 items-center justify-between">
            {/* Logo and Nepal Flag */}
            <div className="flex items-center gap-3 lg:gap-4">
              <Link to="/" className="flex items-center group">
                {logoSettings.show_logo_image && logoSettings.logo_url && (
                  <div className="relative">
                    <img
                      src={logoSettings.logo_url}
                      alt="Logo"
                      style={{
                        width: `${logoSettings.logo_width}px`,
                        height: `${logoSettings.logo_height}px`
                      }}
                      className="max-h-14 max-w-[230px] object-contain transition-opacity duration-200 group-hover:opacity-90"
                    />
                  </div>
                )}
              </Link>
              
              {/* Animated Nepal flag with a static reduced-motion fallback */}
              <div className="relative group">
                <div
                  className="flex items-center"
                  title="Proudly Nepali"
                >
                  {prefersReducedMotion ? (
                    <NepalFlag className="h-8 w-auto opacity-90 transition-all duration-200 group-hover:opacity-100 sm:h-9 lg:h-10" />
                  ) : (
                    <img
                      src="/nepal-flag-wave.webp"
                      alt="Nepal flag waving"
                      className="h-8 w-8 object-contain opacity-90 transition-opacity duration-200 group-hover:opacity-100 sm:h-9 sm:w-9 lg:h-10 lg:w-10"
                    />
                  )}
                </div>
                
                {/* Tooltip */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                  Proudly Nepali 🇳🇵
                </div>
              </div>
            </div>

            {/* Main Navigation Links */}
            <div className="hidden items-center space-x-7 lg:flex">
              {NAVIGATION_ITEMS.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative text-sm font-bold transition-all duration-200 xl:text-base py-2 ${
                    location.pathname === item.href
                      ? 'text-crimson'
                      : 'text-gray-700 hover:text-crimson'
                  }`}
                >
                  {item.name}
                  {/* Active indicator */}
                  {location.pathname === item.href && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-crimson rounded-full"></div>
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center">
              <Link to="/contact">
                <Button variant="primary" size="sm" className="rounded-full px-5 shadow-md">
                  Enroll Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <div className="lg:hidden">
              <button
                onClick={toggleMenu}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-700 transition-colors duration-200 hover:border-red-200 hover:text-crimson"
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Menu - Side Drawer with Smooth Animation */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 touch-none"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Side Menu */}
            <div
              className="lg:hidden w-72 max-w-[90vw] bg-white shadow-2xl z-50 touch-pan-y"
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                height: '100vh',
                minHeight: '100vh',
                maxHeight: '100vh',
                transform: 'translateX(100%)',
                animation: isMenuOpen ? 'slideInFromRight 0.3s ease-out forwards' : 'slideOutToRight 0.3s ease-out forwards'
              }}
            >
              <div className="flex flex-col w-full h-full" style={{ height: '100vh', minHeight: '100vh' }}>
                {/* Menu Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-2">
                    {logoSettings.show_logo_image && logoSettings.logo_url && (
                      <div className="relative">
                        <img
                          src={logoSettings.logo_url}
                          alt="Logo"
                          className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                        />
                      </div>
                    )}
                    {/* Nepal Flag in mobile menu */}
                    {prefersReducedMotion ? (
                      <NepalFlag className="h-6 w-auto opacity-90 sm:h-7" />
                    ) : (
                      <img
                        src="/nepal-flag-wave.webp"
                        alt="Nepal flag waving"
                        className="h-6 w-6 object-contain opacity-90 sm:h-7 sm:w-7"
                      />
                    )}
                  </div>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors touch-manipulation"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>

                {/* Menu Content - Scrollable Top Section */}
                <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
                  {/* Mobile Navigation Links */}
                  <div className="space-y-1 sm:space-y-2">
                    {NAVIGATION_ITEMS.map((item, index) => (
                      <div key={item.name}>
                        <Link
                          to={item.href}
                          className={`block py-3 px-3 sm:px-4 text-sm sm:text-base font-medium rounded-lg transition-colors duration-200 touch-manipulation ${
                            location.pathname === item.href
                              ? 'text-crimson bg-crimson/5'
                              : 'text-gray-700 hover:text-crimson hover:bg-gray-50 active:bg-gray-100'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Fixed Section */}
                <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
                  {/* Mobile Contact Info */}
                  <div className="mb-3 p-3 sm:p-4 bg-white rounded-lg shadow-sm">
                    <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600">
                      <a href={`tel:${contactInfo.phone}`} className="flex items-center space-x-2 hover:text-crimson/80 transition-colors">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-crimson flex-shrink-0" />
                        <span className="leading-tight">{contactInfo.phone}</span>
                      </a>
                      <a href={`mailto:${contactInfo.email}`} className="flex items-center space-x-2 hover:text-crimson/80 transition-colors">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-crimson flex-shrink-0" />
                        <span className="leading-tight">{contactInfo.email}</span>
                      </a>
                      <a href="https://maps.app.goo.gl/JEGWuKF1X14tMd3J7" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-crimson/80 transition-colors">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-crimson flex-shrink-0" />
                        <span className="leading-tight">{contactInfo.address}</span>
                      </a>
                    </div>
                  </div>
                  
                  {/* Mobile Login & Admission */}
                  <div className="mb-3 space-y-2">
                    <Link
                      to="/admission"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 px-3 sm:px-4 text-sm sm:text-base font-medium rounded-lg transition-colors duration-200 touch-manipulation text-gray-700 hover:text-crimson hover:bg-white active:bg-gray-100 border border-gray-200 bg-white"
                    >
                      Admission Center
                    </Link>
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 px-3 sm:px-4 text-sm sm:text-base font-medium rounded-lg transition-colors duration-200 touch-manipulation text-gray-700 hover:text-crimson hover:bg-white active:bg-gray-100 border border-gray-200 bg-white"
                    >
                      Login
                    </Link>
                  </div>

                  {/* Mobile CTA */}
                  <div className="mb-3">
                    <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="primary" size="sm" className="w-full touch-manipulation">
                        Enroll Now
                      </Button>
                    </Link>
                  </div>

                  {/* Mobile Social Links - Only show if header display is enabled */}
                  {socialSettings.show_social_links_header && (
                    <div className="flex justify-center space-x-3 sm:space-x-4 pt-1 mb-12">
                      {!socialLoading && activeSocialLinks.length > 0 ? (
                        // Show admin-managed social links when available
                        activeSocialLinks.map((link) => {
                          const IconComponent = getIcon(link.icon_name)
                          return (
                            <a
                              key={link.id}
                              href={link.url}
                              className="p-2 text-gray-600 hover:text-crimson transition-colors touch-manipulation"
                              title={link.platform_name}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                            </a>
                          )
                        })
                      ) : (
                        // Show default social links when no admin links are configured
                        <>
                          <a
                            href="https://facebook.com"
                            className="p-2 text-gray-600 hover:text-crimson transition-colors touch-manipulation"
                            title="Facebook"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
                          </a>
                          <a
                            href="https://twitter.com"
                            className="p-2 text-gray-600 hover:text-crimson transition-colors touch-manipulation"
                            title="Twitter"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                          </a>
                          <a
                            href="https://instagram.com"
                            className="p-2 text-gray-600 hover:text-crimson transition-colors touch-manipulation"
                            title="Instagram"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
                          </a>
                          <a
                            href="https://linkedin.com"
                            className="p-2 text-gray-600 hover:text-crimson transition-colors touch-manipulation"
                            title="LinkedIn"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navigation
